import { useEffect, useState } from 'react';

/** Initiales de repli : 2 lettres du nom (ou de l'email à défaut). */
export function getInitials(name?: string | null, email?: string | null): string {
  if (name?.trim()) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (email ?? '').slice(0, 2).toUpperCase() || '?';
}

interface AvatarProps {
  avatarUrl?: string | null;
  /** displayName — sert aux initiales de repli. */
  name?: string | null;
  email?: string | null;
  /** Taille + forme du conteneur (appliqué à l'image ET au repli). Ex. "w-7 h-7". */
  className?: string;
  /** Taille de police des initiales de repli. Ex. "text-[10px]". */
  textClassName?: string;
}

/**
 * Avatar utilisateur résilient.
 * - Affiche `avatarUrl` (photo uploadée ou image Google) avec
 *   `referrerPolicy="no-referrer"` — sinon `lh3.googleusercontent.com`
 *   renvoie 403 quand un Referer est envoyé et l'image casse.
 * - Si l'URL est absente OU si le chargement échoue (`onError`), bascule
 *   sur un disque dégradé avec les initiales (cohérent avec le branding).
 */
export default function Avatar({
  avatarUrl,
  name,
  email,
  className = 'w-9 h-9',
  textClassName = 'text-xs',
}: AvatarProps) {
  const [failed, setFailed] = useState(false);

  // Réinitialise l'état d'erreur quand l'URL change (ex. après un upload).
  useEffect(() => setFailed(false), [avatarUrl]);

  if (avatarUrl && !failed) {
    return (
      <img
        src={avatarUrl}
        alt=""
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
        draggable={false}
        className={`${className} rounded-full object-cover shrink-0 select-none`}
      />
    );
  }

  return (
    <div
      className={`avatar-fallback ${className} rounded-full bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center text-white font-bold ${textClassName} select-none shrink-0`}
    >
      {getInitials(name, email)}
    </div>
  );
}
